import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ShieldCheck, Gem, Sparkles, Star, ChevronDown } from "lucide-react";
import { api, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import GoldDust from "../components/GoldDust";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1768845431893-8e28ed7bf79e?q=80&w=1400&auto=format&fit=crop";
const EDIT1 = "https://images.unsplash.com/photo-1774688398553-873fe01a60de?q=80&w=1400&auto=format&fit=crop";
const EDIT2 = "https://images.unsplash.com/photo-1788562767053-6d185dad6e2e?q=80&w=1200&auto=format&fit=crop";
const KINTSUGI = "https://images.unsplash.com/photo-1779878603885-f211807da45e?q=80&w=1200&auto=format&fit=crop";
const SPIRAL = "https://images.unsplash.com/photo-1761437855441-c7597722cfea?q=80&w=1200&auto=format&fit=crop";
const HOOPS = "https://images.unsplash.com/photo-1765064067555-3a9e7b4d338c?q=80&w=1200&auto=format&fit=crop";
const EMBER = "https://images.unsplash.com/photo-1782988112531-a189b256440e?q=80&w=1200&auto=format&fit=crop";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const { add } = useCart();
  const navigate = useNavigate();
  const heroRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const scrollY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const heroFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/products", { params: { featured: "true" } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const marqueeItems = [
    "Jwellary Haute Imitation",
    "18k Gold Plating",
    "Anti-Tarnish Promise",
    "The Gilded Edit",
    "Crafted in India",
  ];

  const spotlight = featured[1] || featured[0];

  return (
    <main className="overflow-x-clip">
      {/* ---------- HERO ---------- */}
      <section
        ref={heroRef}
        onMouseMove={onMove}
        className="relative min-h-screen flex items-center pt-[68px]"
        data-testid="home-hero"
      >
        <GoldDust density={80} />
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center py-16 lg:py-0">
          <motion.div style={{ opacity: heroFade }} className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="eyebrow mb-6"
            >
              Atelier of Imitation Fine Jewellery
            </motion.p>
            <h1 className="font-serif font-light text-[clamp(2.9rem,7vw,6.2rem)] leading-[0.98] tracking-tight text-zinc-100">
              <MaskedLines
                lines={[
                  <span key="a">Wear the</span>,
                  <span key="b" className="italic text-gold-gradient pr-2">Extraordinary,</span>,
                  <span key="c">Every Day.</span>,
                ]}
                baseDelay={0.25}
                stagger={0.2}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95 }}
              className="mt-7 max-w-md text-base leading-relaxed text-zinc-400"
            >
              Hand-finished chokers, hoops, rings and bangles — plated in 2-micron 18k gold,
              made to be worn tonight and treasured for years.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/shop" className="btn-gold" data-testid="hero-cta-shop">
                Shop the Collection <ArrowRight size={16} />
              </Link>
              <button
                className="btn-ghost"
                data-testid="hero-cta-story"
                onClick={() => document.getElementById("story")?.scrollIntoView({ behavior: "smooth" })}
              >
                Our Story
              </button>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
              style={{ x: imgX, y: scrollY }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="relative">
                <div className="rounded-t-[999px] rounded-b-2xl overflow-hidden border border-[#d4af37]/35 shadow-[0_40px_120px_rgba(212,175,55,0.15)]">
                  <img
                    src={HERO_IMG}
                    alt="Jwellary gold necklace editorial"
                    className="w-full h-[520px] lg:h-[620px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 via-transparent to-transparent" />
                </div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 1.3 }}
                  className="glass absolute -left-4 lg:-left-12 bottom-16 rounded-xl gold-hairline px-5 py-4"
                  data-testid="hero-floating-card"
                >
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]">Plating</p>
                  <p className="font-serif text-xl text-zinc-100 mt-1">2-micron 18k gold</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 1.45 }}
                  className="glass absolute right-3 top-16 rounded-xl gold-hairline px-5 py-4"
                >
                  <div className="flex items-center gap-1.5 text-[#d4af37]">
                    {[...Array(5)].map((_, i) => <Star key={i} size={11} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <p className="font-serif text-lg text-zinc-100 mt-1.5">Loved by our circle</p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#d4af37]/70"
        >
          <ChevronDown size={22} strokeWidth={1.4} />
        </motion.div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <Marquee items={marqueeItems} />

      {/* ---------- FEATURED ---------- */}
      <section className="py-24 md:py-32" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="eyebrow mb-4">Signature Pieces</p>
              <h2 className="font-serif font-light text-3xl md:text-5xl text-zinc-100 leading-tight">
                The <span className="italic text-gold-gradient">Signature</span> Edit
              </h2>
            </div>
            <Link to="/shop" className="text-sm tracking-[0.14em] uppercase text-[#f3e5ab] border-b border-[#d4af37]/40 pb-1 hover:border-[#d4af37] transition-colors" data-testid="featured-view-all">
              View All
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* ---------- BENTO ---------- */}
      <section id="gilded-edit" className="pb-24 md:pb-32 scroll-mt-20" data-testid="home-bento">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="mb-12">
            <p className="eyebrow mb-4">Curated Stories</p>
            <h2 className="font-serif font-light text-3xl md:text-5xl text-zinc-100 leading-tight">
              The <span className="italic text-gold-gradient">Gilded</span> Edit
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-4 md:grid-rows-2 gap-4 md:[&>*]:h-full">
            <Reveal className="md:col-span-2 md:row-span-2" delay={0}>
              <BentoTile img={EDIT1} tall title="Evening Story" sub="Statement chokers & drops for after-dark" testid="bento-evening" />
            </Reveal>
            <Reveal className="md:col-span-2" delay={0.1}>
              <BentoTile img={HOOPS} title="The Festive Capsule" sub="Hoops that carry the season" testid="bento-festive" />
            </Reveal>
            <Reveal delay={0.2}>
              <BentoTile img={EMBER} title="Stack & Layer" sub="Rings, worn your way" testid="bento-stack" />
            </Reveal>
            <Reveal delay={0.3}>
              {spotlight ? (
                <button
                  onClick={() => navigate(`/product/${spotlight.id}`)}
                  data-testid="bento-product-spotlight"
                  className="group relative w-full h-full min-h-[220px] md:min-h-0 rounded-xl overflow-hidden border border-[#d4af37]/25 text-left"
                >
                  <img src={spotlight.images?.[0]} alt={spotlight.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/90 via-[#0A0A0B]/30 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <p className="eyebrow mb-1.5">Piece in Focus</p>
                    <p className="font-serif text-xl text-zinc-100">{spotlight.name}</p>
                    <p className="text-sm text-[#f3e5ab] mt-1">{inr(spotlight.price)}</p>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`bento-spotlight-add`}
                    onClick={(e) => { e.stopPropagation(); add(spotlight); }}
                    className="absolute top-3 right-3 p-2.5 rounded-full bg-[#d4af37] text-[#0a0a0b] opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 5v14M5 12h14"/></svg>
                  </span>
                </button>
              ) : (
                <div className="w-full h-full min-h-[220px] rounded-xl bg-[#121215] animate-pulse" />
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- STORY ---------- */}
      <section id="story" className="py-24 md:py-32 scroll-mt-20 border-t border-[#d4af37]/10 bg-[#0c0c0e]" data-testid="home-story">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-[#d4af37]/25">
                <motion.img
                  src={KINTSUGI}
                  alt="Gold plated craft close-up"
                  loading="lazy"
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full h-[420px] md:h-[540px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 md:right-8 w-40 h-40 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.35)_0%,transparent_70%)] blur-xl" />
              <div className="absolute -top-6 -left-2 w-24 h-24 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.25)_0%,transparent_70%)] blur-lg" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow mb-4">The Atelier</p>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight text-zinc-100">
              Gilded by <span className="italic text-gold-gradient">craft</span>, not by mine.
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Every Jwellary piece begins as solid brass, is shaped and polished by hand, then
              bathed in 2-micron 18k gold with an anti-tarnish seal. The result: jewellery that
              reads like fine gold, wears like a whisper, and costs like a celebration.
            </p>
            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              {[
                { icon: ShieldCheck, t: "Anti-Tarnish", d: "Sealed against time and air" },
                { icon: Gem, t: "Hand-Set Stones", d: "Placed one facet at a time" },
                { icon: Sparkles, t: "Skin-Safe", d: "Nickel-free, kind finishes" },
              ].map((f) => (
                <div key={f.t} className="rounded-xl border border-[#d4af37]/15 bg-[#101013] p-5 hover:border-[#d4af37]/45 transition-colors duration-300">
                  <f.icon size={20} strokeWidth={1.5} className="text-[#d4af37]" />
                  <p className="font-serif text-lg text-zinc-100 mt-3">{f.t}</p>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
            <Link to="/shop" className="btn-ghost mt-10" data-testid="story-cta-shop">
              Explore Every Piece <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- EDITORIAL STRIP ---------- */}
      <section className="relative h-[380px] md:h-[460px] overflow-hidden" data-testid="home-editorial">
        <motion.img
          src={SPIRAL}
          alt="Gold spiral editorial"
          loading="lazy"
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0B]/70" />
        <div className="relative h-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col items-start justify-center">
          <Reveal>
            <p className="eyebrow mb-4">New Season</p>
            <p className="font-serif font-light italic text-4xl md:text-6xl text-zinc-100 max-w-2xl leading-tight">
              "Gold, for the every day."
            </p>
            <Link to="/shop" className="btn-gold mt-8" data-testid="editorial-cta-shop">
              Discover the Edit <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- NEWSLETTER ---------- */}
      <section className="py-24 md:py-28" data-testid="home-newsletter">
        <div className="max-w-2xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="eyebrow mb-4">The Golden Circle</p>
            <h2 className="font-serif font-light text-3xl md:text-5xl text-zinc-100">
              First light on <span className="italic text-gold-gradient">new pieces</span>
            </h2>
            <p className="mt-5 text-zinc-500 text-sm leading-relaxed">
              Join the list for early access to limited drops, styling notes and members-only pricing.
            </p>
            <NewsletterForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

const BentoTile = ({ img, title, sub, tall, testid }) => (
  <Link
    to="/shop"
    data-testid={testid}
    className={`group relative block w-full h-full min-h-[220px] ${tall ? "min-h-[340px] md:min-h-0" : ""} rounded-xl overflow-hidden border border-[#d4af37]/20 transition-all duration-500 hover:border-[#d4af37]/60`}
  >
    <img src={img} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/90 via-[#0A0A0B]/25 to-transparent" />
    <div className="absolute bottom-0 p-5 md:p-6">
      <p className="font-serif text-2xl text-zinc-100">{title}</p>
      <p className="text-xs text-zinc-400 mt-1 tracking-wide">{sub}</p>
      <span className="inline-block mt-3 text-[11px] tracking-[0.2em] uppercase text-[#f3e5ab] border-b border-[#d4af37]/40 pb-0.5">
        Explore
      </span>
    </div>
  </Link>
);

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Golden Circle");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="input-dark flex-1"
        data-testid="newsletter-email-input"
        aria-label="Email address"
      />
      <button type="submit" disabled={busy} className="btn-gold" data-testid="newsletter-submit-button">
        {busy ? "Joining..." : "Join"}
      </button>
    </form>
  );
};

export default Home;
